<script setup lang="ts">
import { ref, watch, nextTick } from "vue";
import { marked } from "marked";
import type { Message } from "../types/chat";

/**
 * MessageList is a presentational component responsible for rendering
 * a list of chat messages.
 */

const props = defineProps<{
  messages: Message[];
  canSelectWinner?: boolean;
  selectedWinnerId?: string | null;
}>();

const emit = defineEmits<{
  selectWinner: [messageId: string, content: string];
}>();

const messageListRef = ref<HTMLElement | null>(null);
const copiedMessageId = ref<string | null>(null);

const formatDate = (date: Date): string => {
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const formatStats = (stats: {
  wordCount: number;
  characterCount: number;
  characterCountNoSpaces: number;
}) => {
  return `${stats.wordCount} sanaa · ${stats.characterCount} merkkiä`;
};

// Function to render AI responses as markdown with fallback to plain text
const renderMessageContent = (message: Message): string => {
  // For user messages, return as-is (they're already HTML)
  if (message.sender === "user") {
    return message.content;
  }

  // For AI messages, try to render as markdown
  try {
    // Check if content already contains HTML tags (already processed)
    if (
      message.content.includes('<div class="loading-spinner">') ||
      message.content.includes("<p>") ||
      message.content.includes("<div>")
    ) {
      return message.content;
    }

    // Try to parse as markdown
    return marked(message.content);
  } catch (error) {
    console.warn(
      "Failed to parse markdown, falling back to plain text:",
      error,
    );
    // Fallback: wrap in paragraph tag for consistent styling
    return `<p>${message.content}</p>`;
  }
};

// Function to extract plain text from message content
const getPlainTextContent = (message: Message): string => {
  // For loading messages, return a simple message
  if (message.content.includes('<div class="loading-spinner">')) {
    return "Ladataan...";
  }

  // Create a temporary div to extract plain text from HTML/markdown
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = renderMessageContent(message);
  return tempDiv.textContent || tempDiv.innerText || message.content;
};

// Function to select a message as winner for final editing
const selectWinner = (message: Message) => {
  // Prefer the structured review field (clean markdown) over message.content,
  // which for older messages without structuredResponse may not be reliable.
  const contentToEdit = message.structuredResponse?.review || message.content;
  emit("selectWinner", message.id, contentToEdit);
};

// Function to copy message content to clipboard
const copyMessage = async (message: Message) => {
  try {
    const plainText = getPlainTextContent(message);
    await navigator.clipboard.writeText(plainText);

    // Show visual feedback
    copiedMessageId.value = message.id;
    setTimeout(() => {
      copiedMessageId.value = null;
    }, 2000);
  } catch (error) {
    console.error("Failed to copy message:", error);
    // Fallback for older browsers
    const textArea = document.createElement("textarea");
    textArea.value = getPlainTextContent(message);
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand("copy");
      copiedMessageId.value = message.id;
      setTimeout(() => {
        copiedMessageId.value = null;
      }, 2000);
    } catch (fallbackError) {
      console.error("Fallback copy also failed:", fallbackError);
    }
    document.body.removeChild(textArea);
  }
};

// Function to scroll to the bottom of the message list
const scrollToBottom = () => {
  if (messageListRef.value) {
    messageListRef.value.scrollTop = messageListRef.value.scrollHeight;
  }
};

// Watch for changes in messages and auto-scroll to bottom
watch(
  () => props.messages,
  () => {
    void nextTick(() => {
      scrollToBottom();
    });
  },
  { deep: true },
);
</script>

<template>
  <div class="message-list" ref="messageListRef">
    <div
      v-for="message in messages"
      :key="message.id"
      class="message-container"
      :class="`sender-${message.sender}`"
    >
      <div class="message-bubble">
        <!-- Display structured response for AI messages when available -->
        <div
          v-if="message.sender === 'ai' && message.structuredResponse"
          class="structured-response"
        >
          <div class="review-section">
            <div
              class="message-content"
              v-html="
                renderMessageContent({
                  ...message,
                  content: message.structuredResponse.review,
                })
              "
            ></div>
          </div>
          <div
            v-if="message.structuredResponse.comment.trim()"
            class="comment-section"
          >
            <div class="comment-header">💭 Kommentti opettajalle:</div>
            <div class="comment-content">
              {{ message.structuredResponse.comment }}
            </div>
          </div>
        </div>
        <!-- Fallback to regular content display -->
        <div
          v-else
          class="message-content"
          v-html="renderMessageContent(message)"
        ></div>
        <div class="message-meta">
          <span class="timestamp">{{ formatDate(message.timestamp) }}</span>
          <span v-if="message.sender === 'ai' && message.stats" class="stats">
            {{ formatStats(message.stats) }}
          </span>
        </div>
        <div class="message-actions">
          <!-- Select button for unselected AI messages -->
          <button
            v-if="
              message.sender === 'ai' && canSelectWinner && !selectedWinnerId
            "
            class="winner-button"
            @click="selectWinner(message)"
            title="Valitse viimeisteltäväksi"
          >
            ✨ Valitse
          </button>

          <!-- Change selection button for other AI messages when one is already selected -->
          <button
            v-if="
              message.sender === 'ai' &&
              canSelectWinner &&
              selectedWinnerId &&
              selectedWinnerId !== message.id
            "
            class="winner-change-button"
            @click="selectWinner(message)"
            title="Vaihda valinta tähän viestiin"
          >
            🔄 Vaihda
          </button>

          <!-- Currently selected message indicator -->
          <button
            v-if="message.sender === 'ai' && selectedWinnerId === message.id"
            class="winner-selected"
            disabled
            title="Valittu viimeisteltäväksi"
          >
            ✅ Valittu
          </button>
          <button
            class="copy-button"
            @click="copyMessage(message)"
            :title="
              copiedMessageId === message.id ? 'Kopioitu!' : 'Kopioi viesti'
            "
          >
            <span v-if="copiedMessageId === message.id">✓</span>
            <span v-else>📋</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.message-list {
  flex-grow: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.message-container {
  display: flex;
  max-width: 75%;
}

.message-bubble {
  padding: 10px 15px;
  border-radius: 18px;
  color: #333;
  position: relative;
}

.message-content {
  /* This ensures the HTML from Tiptap renders correctly */
  white-space: pre-wrap;
}

/* Styling for AI messages */
.sender-ai {
  justify-content: flex-start;
}
.sender-ai .message-bubble {
  background-color: #e9e9eb;
  border-bottom-left-radius: 4px;
}

/* Styling for user messages */
.sender-user {
  justify-content: flex-end;
  align-self: flex-end;
}
.sender-user .message-bubble {
  background-color: #007aff;
  color: white;
  border-bottom-right-radius: 4px;
}

.message-meta {
  margin-top: 5px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: right;
}

.timestamp {
  font-size: 0.75rem;
  opacity: 0.7;
}

.stats {
  font-size: 0.7rem;
  opacity: 0.6;
  font-style: italic;
  color: #666;
}

.sender-user .stats {
  color: rgba(255, 255, 255, 0.8);
}

/* Markdown styling for AI messages */
.message-content :deep(h1),
.message-content :deep(h2),
.message-content :deep(h3),
.message-content :deep(h4),
.message-content :deep(h5),
.message-content :deep(h6) {
  margin: 0.5rem 0 0.25rem 0;
  font-weight: 600;
}

.message-content :deep(h1) {
  font-size: 1.2rem;
}
.message-content :deep(h2) {
  font-size: 1.1rem;
}
.message-content :deep(h3) {
  font-size: 1rem;
}

.message-content :deep(p) {
  margin: 0.25rem 0;
  line-height: 1.4;
}

.message-content :deep(ul),
.message-content :deep(ol) {
  margin: 0.25rem 0;
  padding-left: 1.2rem;
}

.message-content :deep(li) {
  margin-bottom: 0.1rem;
}

.message-content :deep(code) {
  background-color: rgba(0, 0, 0, 0.1);
  padding: 0.1rem 0.2rem;
  border-radius: 3px;
  font-family: monospace;
  font-size: 0.9em;
}

.message-content :deep(pre) {
  background-color: rgba(0, 0, 0, 0.05);
  padding: 0.5rem;
  border-radius: 4px;
  overflow-x: auto;
  margin: 0.25rem 0;
}

.message-content :deep(pre code) {
  background-color: transparent;
  padding: 0;
}

.message-content :deep(blockquote) {
  border-left: 3px solid #ccc;
  margin: 0.25rem 0;
  padding-left: 0.5rem;
  color: #666;
}

.sender-user .message-content :deep(code) {
  background-color: rgba(255, 255, 255, 0.2);
}

.sender-user .message-content :deep(pre) {
  background-color: rgba(255, 255, 255, 0.1);
}

.sender-user .message-content :deep(blockquote) {
  border-left-color: rgba(255, 255, 255, 0.5);
  color: rgba(255, 255, 255, 0.9);
}

/* Message actions container */
.message-actions {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: all 0.2s ease;
}

/* Copy button styling */
.copy-button {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  padding: 4px 6px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Winner selection button styling */
.winner-button {
  background: linear-gradient(135deg, #007aff, #5856d6);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 6px 10px;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 600;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(0, 122, 255, 0.3);
}

.winner-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 122, 255, 0.4);
}

/* Winner change button styling */
.winner-change-button {
  background: linear-gradient(135deg, #ff9500, #ff6b35);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 0.8em;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(255, 149, 0, 0.3);
}

.winner-change-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 149, 0, 0.4);
}

/* Winner selected button styling */
.winner-selected {
  background: #34c759;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: default;
  box-shadow: 0 2px 6px rgba(52, 199, 89, 0.3);
}

.message-bubble:hover .message-actions {
  opacity: 1;
}

.copy-button:hover {
  background: rgba(255, 255, 255, 1);
  transform: scale(1.05);
}

.copy-button:active {
  transform: scale(0.95);
}

/* User message copy button styling */
.sender-user .copy-button {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
}

.sender-user .copy-button:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Structured response styling */
.structured-response {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.review-section {
  /* Main review content - no special styling needed */
}

.comment-section {
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  padding-top: 12px;
  margin-top: 8px;
}

.comment-header {
  font-weight: 600;
  font-size: 0.9em;
  color: #666;
  margin-bottom: 6px;
}

.comment-content {
  font-style: italic;
  color: #555;
  font-size: 0.95em;
  background: rgba(0, 0, 0, 0.03);
  padding: 8px 12px;
  border-radius: 8px;
  border-left: 3px solid #007aff;
}

/* Adjust comment styling for user messages */
.sender-user .comment-section {
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}

.sender-user .comment-header {
  color: rgba(255, 255, 255, 0.8);
}

.sender-user .comment-content {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
  border-left: 3px solid rgba(255, 255, 255, 0.5);
}
</style>
